import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface UsageSummary {
  readonly totalRulesTracked: number;
  readonly totalExecutions: number;
  readonly totalSuccess: number;
  readonly totalFailure: number;
  readonly totalNoMatch: number;
  readonly totalError: number;
}

interface TopRule {
  readonly ruleId: string;
  readonly ruleCode: string | null;
  readonly ruleName: string | null;
  readonly executionCount: string;
  readonly successCount: string;
}

export function M24RuleAnalyticsDashboardPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [top, setTop] = useState<TopRule[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      apiRequest<{ data: UsageSummary }>('/v1/rule-analytics/summary', { token }),
      apiRequest<{ data: TopRule[] }>('/v1/rule-analytics/top', { token }),
    ])
      .then(([s, t]) => { if (alive) { setSummary(s.data); setTop(t.data); } })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  if (error) return <p role="alert">{error}</p>;
  if (!summary) return <p>{tr('m24.analytics.loading')}</p>;
  return (
    <main aria-label="rule-analytics">
      <h1>{tr('m24.analytics.title')}</h1>
      <section aria-label="analytics-metrics" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
        <article aria-label="rules-tracked"><h2>{tr('m24.analytics.rulesTracked')}</h2><p>{summary.totalRulesTracked}</p></article>
        <article aria-label="total-executions"><h2>{tr('m24.analytics.executions')}</h2><p>{summary.totalExecutions}</p></article>
        <article aria-label="total-success"><h2>{tr('m24.analytics.success')}</h2><p>{summary.totalSuccess}</p></article>
        <article aria-label="total-failure"><h2>{tr('m24.analytics.failure')}</h2><p>{summary.totalFailure}</p></article>
        <article aria-label="total-no-match"><h2>{tr('m24.analytics.noMatch')}</h2><p>{summary.totalNoMatch}</p></article>
        <article aria-label="total-error"><h2>{tr('m24.analytics.error')}</h2><p>{summary.totalError}</p></article>
      </section>
      <section aria-label="top-rules">
        <h2>{tr('m24.analytics.topRules')}</h2>
        <table aria-label="top-rules-table">
          <thead><tr><th>{tr('m24.analytics.colRule')}</th><th>{tr('m24.analytics.colExecutions')}</th><th>{tr('m24.analytics.colSuccesses')}</th></tr></thead>
          <tbody>
            {top.map((r) => (
              <tr key={r.ruleId}>
                <td>{[r.ruleCode, r.ruleName].filter(Boolean).join(' \u2014 ')}</td>
                <td>{r.executionCount}</td>
                <td>{r.successCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </main>
  );
}
